import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PeopleIcon from "@mui/icons-material/People";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function SquarespaceCreateCustomer() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [formData, setFormData] = React.useState({
    email: "",
    firstName: "",
    lastName: "",
    metadataCountry: "US",
    metadataIpAddress: "0.0.0.0",
    metadataUserAgent: "BryteBridge Admin",
  });
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!formData.firstName.trim()) {
      setError("First name is required");
      return;
    }
    if (!formData.lastName.trim()) {
      setError("Last name is required");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Prepare request body - email, firstName, lastName required; metadata for Squarespace
      const requestBody = {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        metadata: {
          country: formData.metadataCountry.trim() || "US",
          ipAddress: formData.metadataIpAddress.trim() || "0.0.0.0",
          userAgent: formData.metadataUserAgent.trim() || "BryteBridge Admin",
        },
      };

      const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = "Failed to create customer";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Use default error message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const customer = result.data || result;
      const customerId = customer.customerId || customer.id || customer.email;

      if (customerId) {
        navigate(`/dashboard/squarespace/customers/${customerId}`);
      } else {
        navigate("/dashboard/squarespace/customers");
      }
    } catch (err) {
      console.error("Error creating customer:", err);
      setError(err.message || "Failed to create customer");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace/customers"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Customers
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Create Customer
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Create Squarespace Customer"
              description="Add a new customer to Squarespace"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange("email")}
                        required
                        disabled={creating}
                        helperText="Customer email address (required)"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        required
                        disabled={creating}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        required
                        disabled={creating}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                        Metadata (required by Squarespace)
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={formData.metadataCountry}
                        onChange={handleChange("metadataCountry")}
                        disabled={creating}
                        helperText="e.g. US"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="IP Address"
                        value={formData.metadataIpAddress}
                        onChange={handleChange("metadataIpAddress")}
                        disabled={creating}
                        helperText="Customer IP"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label="User Agent"
                        value={formData.metadataUserAgent}
                        onChange={handleChange("metadataUserAgent")}
                        disabled={creating}
                        helperText="Browser/user agent string"
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/dashboard/squarespace/customers")}
                      disabled={creating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={creating ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={creating}
                    >
                      {creating ? "Creating..." : "Create Customer"}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
